import { createPortal } from "react-dom";
import { useEffect, type ReactNode, type HTMLAttributes } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  closeOnBackdrop?: boolean;
};

/** Dialog modal custom (sin Radix). Portal + backdrop + ESC. */
export function Dialog({
  open,
  onOpenChange,
  children,
  size = "md",
  closeOnBackdrop = true,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    "2xl": "max-w-5xl",
    "3xl": "max-w-6xl",
  }[size];

  return createPortal(
    <div
      // rc115 — overflow-x-hidden: en iPhone los selects nativos con opciones
      // largas desbordaban el panel y la página entera paneaba horizontal.
      // El eje Y sigue con scroll normal; el X se corta aquí de raíz.
      // rc136 — móvil (<md): el dialog es una HOJA A PANTALLA COMPLETA. Sin
      // padding ni "flotar" a 10vh; el panel ocupa todo el alto y el pie
      // (DialogFooter) queda pegado abajo con el área segura del iPhone.
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overflow-x-hidden bg-black/50 p-4 pt-[10vh] animate-in fade-in max-md:p-0 max-md:pt-0"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className={cn(
          // rc115 — min-w-0: el panel es flex item y su min-width:auto dejaba
          // que un hijo ancho (select nativo con opciones largas) lo estirara
          // más allá del viewport → la página paneaba horizontal en iPhone.
          "relative w-full min-w-0 rounded-lg border bg-card text-card-foreground shadow-xl",
          // rc136 — hoja móvil: sin bordes redondeados, alto mínimo = viewport,
          // columna flex para que un <form> hijo también estire y el footer
          // sticky tenga a dónde pegarse.
          "max-md:flex max-md:min-h-dvh max-md:max-w-none max-md:flex-col max-md:rounded-none max-md:border-0 max-md:shadow-none",
          "max-md:[&>form]:flex max-md:[&>form]:flex-1 max-md:[&>form]:flex-col",
          "max-md:sheet-in",
          sizeClasses
        )}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          // rc136 — móvil: botón más grande (tap) y debajo de la barra de estado.
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground max-md:right-2 max-md:top-[calc(0.5rem+env(safe-area-inset-top))] max-md:p-2"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // rc136 — móvil: respeta la barra de estado del iPhone y deja hueco a la X.
  return <div className={cn("flex flex-col space-y-1 p-5 pb-3 max-md:pr-12 max-md:pt-[calc(1rem+env(safe-area-inset-top))]", className)} {...props} />;
}

export function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-base font-semibold leading-none", className)} {...props} />;
}

export function DialogDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-xs text-muted-foreground", className)} {...props} />;
}

export function DialogContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // rc136 — móvil: el contenido crece y empuja el footer al fondo.
  return <div className={cn("px-5 pb-4 max-md:flex-1 max-md:px-4", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t bg-muted/30 p-3",
        // rc136 — móvil: pie pegajoso al fondo de la hoja (los botones Guardar/
        // Cancelar siempre a la vista en formularios largos), fondo sólido y
        // padding del home indicator.
        "max-md:sticky max-md:bottom-0 max-md:z-10 max-md:mt-auto max-md:bg-card max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]",
        className
      )}
      {...props}
    />
  );
}
